import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Link,
  Pagination,
} from '@mui/material'
import {
  CheckCircle as ActiveIcon,
  FileDownload as ExportIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material'
import { useList, useNotification, useDelete } from '@refinedev/core'
import { format } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'
import ApplyDiscountModal from './ApplyDiscountModal'

interface ActiveDiscount {
  org_uuid: string
  org_name: string
  discount_code: string
  original_amount: number
  discount_amount: number
  final_amount: number
  applied_at: string
  expires_at: string
}

interface ActiveDiscountsViewProps {
  discountCodeID?: number
  onDataChange?: () => void
  refreshTrigger?: number
}

export default function ActiveDiscountsView({
  discountCodeID,
  onDataChange,
  refreshTrigger,
}: ActiveDiscountsViewProps) {
  const { open } = useNotification()
  const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set())
  const [applyModalOpen, setApplyModalOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removingOrgUuid, setRemovingOrgUuid] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // Fetch organisations with active discounts
  const { data, isLoading, refetch } = useList<ActiveDiscount>({
    resource: 'discount-codes/active-orgs',
    pagination: {
      current: currentPage,
      pageSize: 10,
    },
    meta: {
      discount_code_id: discountCodeID,
    },
  })

  const { mutate: removeDiscount } = useDelete()

  const activeDiscounts = data?.data || []

  const total = data?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  // Refetch when refresh trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      refetch()
    }
  }, [refreshTrigger, refetch])

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ): void => {
    setCurrentPage(page)
  }

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedOrgs(new Set(activeDiscounts.map((d) => d.org_uuid)))
      } else {
        setSelectedOrgs(new Set())
      }
    },
    [activeDiscounts]
  )

  const handleSelectOne = useCallback((orgUuid: string) => {
    setSelectedOrgs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(orgUuid)) {
        newSet.delete(orgUuid)
      } else {
        newSet.add(orgUuid)
      }
      return newSet
    })
  }, [])

  const handleExport = useCallback(() => {
    // Create CSV content
    const headers = [
      'Organisation Name',
      'Organisation UUID',
      'Discount Code',
      'Original Amount',
      'Discount Amount',
      'Final Amount',
      'Applied Date',
      'Expires Date',
    ]

    const rows = activeDiscounts
      .filter((d) => selectedOrgs.size === 0 || selectedOrgs.has(d.org_uuid))
      .map((d) => [
        d.org_name,
        d.org_uuid,
        d.discount_code,
        d.original_amount.toFixed(2),
        d.discount_amount.toFixed(2),
        d.final_amount.toFixed(2),
        format(new Date(d.applied_at), 'yyyy-MM-dd'),
        format(new Date(d.expires_at), 'yyyy-MM-dd'),
      ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `active-discounts-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    open?.({
      type: 'success',
      message: 'Export completed',
    })
  }, [activeDiscounts, selectedOrgs, open])

  const handleRemoveDiscount = useCallback((orgUuid: string) => {
    setRemovingOrgUuid(orgUuid)
    setRemoveDialogOpen(true)
  }, [])

  const confirmRemoveDiscount = useCallback(async () => {
    if (!removingOrgUuid) return

    try {
      await removeDiscount({
        resource: `discount-codes/remove`,
        id: removingOrgUuid,
      })

      open?.({
        type: 'success',
        message: 'Discount successfully removed from organisation',
      })

      refetch()
      // Also refresh after a short delay to ensure backend has processed
      setTimeout(() => {
        refetch()
        onDataChange?.()
      }, 500)
    } catch (error) {
      open?.({
        type: 'error',
        message: 'Failed to remove discount',
      })
    } finally {
      setRemoveDialogOpen(false)
      setRemovingOrgUuid(null)
    }
  }, [removingOrgUuid, removeDiscount, open, refetch])

  const handleApplySuccess = useCallback(() => {
    // Force refetch the active discounts data immediately
    refetch()

    // Also refresh after a short delay to ensure backend has processed
    setTimeout(() => {
      refetch()
      onDataChange?.()
    }, 1000)
  }, [refetch, onDataChange])

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box mt={3}>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <ActiveIcon color="success" />
              <Typography variant="h5" fontWeight={600}>
                Active Discounts
              </Typography>
              <Chip
                label={`${activeDiscounts.length} organisations`}
                color="success"
                size="small"
              />
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setApplyModalOpen(true)}
                color="primary"
              >
                Apply Discount
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                disabled={activeDiscounts.length === 0}
              >
                Export{' '}
                {selectedOrgs.size > 0 ? `(${selectedOrgs.size})` : 'All'}
              </Button>
            </Box>
          </Box>

          {/* Info Alert */}
          <Alert severity="success" sx={{ mb: 3 }}>
            These organisations currently have{' '}
            {discountCodeID ? 'this discount' : 'active discounts'} applied to
            their subscriptions.
          </Alert>

          {/* Active Discounts Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedOrgs.size > 0 &&
                        selectedOrgs.size < activeDiscounts.length
                      }
                      checked={
                        activeDiscounts.length > 0 &&
                        selectedOrgs.size === activeDiscounts.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Discount Code</TableCell>
                  <TableCell align="right">Original Price</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Discounted Price</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeDiscounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No active discounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activeDiscounts.map((discount) => (
                    <TableRow key={discount.org_uuid}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOrgs.has(discount.org_uuid)}
                          onChange={() => handleSelectOne(discount.org_uuid)}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/orgs/${discount.org_uuid}`}
                          underline="hover"
                          color="primary"
                          sx={{ textDecoration: 'none' }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary"
                          >
                            {discount.org_name}
                          </Typography>
                        </Link>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {discount.org_uuid}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={discount.discount_code}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${discount.original_amount.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          -${discount.discount_amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          ${discount.final_amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(discount.applied_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          {format(new Date(discount.expires_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove discount">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() =>
                              handleRemoveDiscount(discount.org_uuid)
                            }
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Summary */}
          {activeDiscounts.length > 0 && (
            <Box mt={3}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Total Discount Value:</strong> $
                  {activeDiscounts
                    .reduce((sum, d) => sum + d.discount_amount, 0)
                    .toFixed(2)}{' '}
                  in active discounts across {activeDiscounts.length}{' '}
                  organisations
                </Typography>
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Apply Discount Modal */}
      <ApplyDiscountModal
        discountCodeID={discountCodeID}
        open={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        onSuccess={handleApplySuccess}
      />

      {/* Remove Discount Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
      >
        <DialogTitle>Remove Discount?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the discount from this organisation?
            This action will immediately end their discount and cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmRemoveDiscount}
            variant="contained"
            color="error"
          >
            Remove Discount
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
